import { useCallback, useEffect, useRef } from "react";
import { parseSkillMessageData, type Notification } from "../types";
import styles from "./NotificationToaster.module.css";

/** Auto-dismiss delay; driven by setTimeout, not CSS (see note below). */
const DISMISS_MS = 5000;
/** High-priority skill messages stay up longer so they aren't missed. */
const HIGH_PRIORITY_DISMISS_MS = 10000;

/** Auto-dismiss delay for a toast, or null when it stays until dismissed. */
function dismissDelay(notification: Notification): number | null {
  const priority = parseSkillMessageData(notification)?.priority;
  if (priority === "urgent") {
    return null;
  }
  return priority === "high" ? HIGH_PRIORITY_DISMISS_MS : DISMISS_MS;
}

function intentClass(notification: Notification): string {
  if (parseSkillMessageData(notification)?.priority === "urgent") {
    return styles.error ?? "";
  }
  const byType: Record<string, string | undefined> = {
    approval: styles.approval,
    error: styles.error,
    success: styles.success,
  };
  return byType[notification.type] ?? styles.info ?? "";
}

interface ToastCardProps {
  readonly notification: Notification;
  readonly onOpen: (notification: Notification) => void;
  readonly onDismiss: (id: number) => void;
}

/**
 * A single toast card. Owns its own auto-dismiss timer so hover-to-pause works
 * per card; an urgent skill message has no timer and stays until dismissed. Timing is driven by setTimeout rather than a CSS animation: Office
 * WebViews (notably Outlook Desktop's WebView2 with system "reduce motion")
 * collapse animation durations to ~0ms, which is the bug that ChatToaster
 * documents and works around.
 */
function ToastCard({ notification, onOpen, onDismiss }: ToastCardProps) {
  const timerRef = useRef<number | null>(null);
  const delay = dismissDelay(notification);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      globalThis.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clear();
    if (delay === null) {
      return;
    }
    timerRef.current = globalThis.setTimeout(() => onDismiss(notification.id), delay);
  }, [clear, onDismiss, notification.id, delay]);

  useEffect(() => {
    schedule();
    return clear;
  }, [schedule, clear]);

  return (
    // <output> carries an implicit role="status" + aria-live="polite", so the
    // toast is announced without an explicit role (keeps Sonar's a11y rule happy).
    <output
      className={`${styles.toast} ${intentClass(notification)}`}
      onMouseEnter={clear}
      onMouseLeave={schedule}
    >
      <button type="button" className={styles.body} onClick={() => onOpen(notification)}>
        <span className={styles.title}>{notification.title}</span>
        {notification.body && <span className={styles.message}>{notification.body}</span>}
      </button>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={() => onDismiss(notification.id)}
      >
        &times;
      </button>
    </output>
  );
}

interface NotificationToasterProps {
  readonly toasts: Notification[];
  readonly onOpen: (notification: Notification) => void;
  readonly onDismiss: (id: number) => void;
}

/** Fixed top-right stack of live notification toasts. */
export function NotificationToaster({
  toasts,
  onOpen,
  onDismiss,
}: NotificationToasterProps): React.ReactElement | null {
  if (toasts.length === 0) {
    return null;
  }
  return (
    <div className={styles.wrapper}>
      {toasts.map((t) => (
        <ToastCard key={t.id} notification={t} onOpen={onOpen} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
